/**
 * AI Generator configurations for different modules
 * Each module defines:
 * - contextFields: Fields that can be sent as context
 * - generatableFields: Fields that can be generated
 * - options: Available options for generation
 */

export const aiGeneratorConfigs = {
  hse_document: {
    module: 'hse_document',
    buttonLabel: 'Generer innhold med AI',
    icon: 'SparklesIcon',
    contextFields: {
      title: { label: 'Tittel', type: 'string', description: 'Dokumenttittel' },
      description: { label: 'Beskrivelse', type: 'string', description: 'Beskrivelse av dokumentet' },
      discipline: { label: 'Fag/Bransje', type: 'string', description: 'Fag eller bransje' },
      category: { label: 'Kategori', type: 'string', description: 'Kategori' },
      chapter: { label: 'Kapittel', type: 'string', description: 'Kapittelnummer' },
      document_type: { label: 'Dokumenttype', type: 'string', description: 'Type dokument' }
    },
    generatableFields: {
      content: { 
        label: 'Innhold', 
        type: 'html',
        description: 'Generer dokumentinnhold basert på kontekst',
        default: true
      },
      description: {
        label: 'Beskrivelse',
        type: 'string',
        description: 'Generer en beskrivelse av dokumentet',
        default: false
      }
    },
    options: {
      language: {
        label: 'Språk',
        type: 'select',
        options: [
          { value: 'no', label: 'Norsk' },
          { value: 'en', label: 'Engelsk' }
        ],
        default: 'no'
      },
      tone: {
        label: 'Tone',
        type: 'select',
        options: [
          { value: 'professional', label: 'Profesjonell' },
          { value: 'formal', label: 'Formell' },
          { value: 'casual', label: 'Uformell' }
        ],
        default: 'professional'
      },
      length: {
        label: 'Lengde',
        type: 'select',
        options: [
          { value: 'short', label: 'Kort' },
          { value: 'medium', label: 'Middels' },
          { value: 'detailed', label: 'Detaljert' }
        ],
        default: 'detailed'
      },
      html_format: {
        label: 'Format',
        type: 'select',
        options: [
          { value: 'html', label: 'HTML (med formatering)' },
          { value: 'text', label: 'Ren tekst' }
        ],
        default: 'html'
      }
    }
  },
  webpage_page_builder: {
    module: "webpage_page_builder",
    buttonLabel: "Generer side (JSON) med AI",
    icon: "SparklesIcon",
    contextFields: {
      page_title: { label: "Sidetittel", type: "string", description: "Tittel som vises og brukes i kontekst" },
      page_slug: { label: "Slug", type: "string", description: "URL-fragment" },
      meta_description: { label: "Metabeskrivelse", type: "string", description: "SEO / sammendrag" }
    },
    generatableFields: {
      blocks_json: {
        label: "Sideblokker (JSON)",
        type: "json",
        description: "Genererer Page Builder JSON (blokker) basert på kontekst",
        default: true
      }
    },
    options: {
      language: {
        label: "Språk (tekst i blokker)",
        type: "select",
        options: [
          { value: "no", label: "Norsk" },
          { value: "en", label: "Engelsk" },
          { value: "sv", label: "Svensk" }
        ],
        default: "no"
      },
      page_kind: {
        label: "Sidetype",
        type: "select",
        options: [
          { value: "landing", label: "Landing / kampanje" },
          { value: "content", label: "Innhold" },
          { value: "about", label: "Om oss" },
          { value: "contact", label: "Kontakt" }
        ],
        default: "content"
      },
      length: {
        label: "Omfang",
        type: "select",
        options: [
          { value: "short", label: "Kort" },
          { value: "medium", label: "Middels" },
          { value: "detailed", label: "Detaljert" }
        ],
        default: "medium"
      },
      apply_mode: {
        label: "I editoren",
        type: "select",
        options: [
          { value: "replace", label: "Erstatt alle blokker" },
          { value: "append", label: "Legg til nederst" }
        ],
        default: "replace"
      },
      user_goal: {
        label: "Egne instruksjoner (valgfritt)",
        type: "text",
        multiline: true,
        default: "",
        description: "F.eks. tonen, fokus, eller inkluder priser / team"
      }
    }
  },
  guide_flow: {
    module: "guide_flow",
    buttonLabel: "Generer guideflyt (JSON)",
    icon: "SparklesIcon",
    contextFields: {
      guide_title: { label: "Guidetittel", type: "string", description: "Navn på guiden" },
      guide_description: { label: "Beskrivelse", type: "string", description: "Hva guiden skal gjøre" },
      guide_kind: {
        label: "Guidetype (maskin lesbar)",
        type: "string",
        description: "case_onboarding eller field_report — styrer tillatte effekttyper"
      }
    },
    generatableFields: {
      flow_json: {
        label: "Guideflyt (JSON)",
        type: "json",
        description: "Komplett flyt: version, viewport, start_node_id, nodes",
        default: true
      }
    },
    options: {
      language: {
        label: "Språk (spørsmålstekster)",
        type: "select",
        options: [
          { value: "no", label: "Norsk" },
          { value: "en", label: "Engelsk" },
          { value: "sv", label: "Svensk" }
        ],
        default: "no"
      },
      length: {
        label: "Omfang",
        type: "select",
        options: [
          { value: "short", label: "Kort" },
          { value: "medium", label: "Middels" },
          { value: "detailed", label: "Detaljert" }
        ],
        default: "medium"
      },
      user_goal: {
        label: "Egne instruksjoner (valgfritt)",
        type: "text",
        multiline: true,
        default: "",
        description: "Ekstra krav til flyt, grener eller effekter"
      }
    }
  },
  todo: {
    module: 'todo',
    buttonLabel: 'AI for oppgaver',
    icon: 'SparklesIcon',
    contextFields: {
      task_title: { label: 'Tittel', type: 'string', description: 'Oppgavetittel' },
      task_description: { label: 'Beskrivelse', type: 'string', description: 'Beskrivelse' },
      open_tasks: { label: 'Åpne oppgaver', type: 'string', description: 'Liste' },
      hours_available: { label: 'Timer i dag', type: 'string', description: 'Tilgjengelig tid' }
    },
    generatableFields: {
      task_suggestions: {
        label: 'Oppgaveforslag',
        type: 'string',
        description: 'Nye forslag',
        default: true
      },
      time_estimate: {
        label: 'Tidsestimat (timer)',
        type: 'string',
        description: 'Estimat for hovedoppgaven',
        default: false
      },
      daily_plan: {
        label: 'Dagsplan',
        type: 'string',
        description: 'Prioritert rekkefølge',
        default: false
      }
    },
    options: {
      language: {
        label: 'Språk',
        type: 'select',
        options: [
          { value: 'no', label: 'Norsk' },
          { value: 'en', label: 'Engelsk' }
        ],
        default: 'no'
      },
      length: {
        label: 'Lengde',
        type: 'select',
        options: [
          { value: 'short', label: 'Kort' },
          { value: 'medium', label: 'Middels' }
        ],
        default: 'short'
      }
    }
  }
};

/**
 * AI Provider information
 */
export const aiProviders = {
  grok: {
    name: 'Grok AI',
    label: 'Grok AI (xAI)',
    description: 'Krever credits/licenses - ikke gratis for API-bruk',
    cost: 'Betal',
    recommended: false,
    icon: 'SparklesIcon',
    color: 'text-yellow-600',
    warning: 'Bruk av Grok AI koster penger basert på tokens brukt'
  },
  chatgpt: {
    name: 'ChatGPT',
    label: 'ChatGPT (OpenAI)',
    description: 'Standard AI-leverandør - allerede konfigurert',
    cost: 'Betal',
    recommended: true,
    icon: 'ChatBubbleLeftRightIcon',
    color: 'text-blue-600',
    warning: 'Bruk av ChatGPT koster penger basert på tokens brukt'
  }
};
